import { createColumnHelper } from '@tanstack/react-table';
import { tabNames } from '@va/constants';
import { VisitSession } from '@va/dashboard/api-client/Visitors';
import { DetailsCell, ReferrerCell, VideoRecordingButton, VisitorDetailsCell } from '@va/dashboard/components';
import { useCheckPrivacySetting } from '@va/dashboard/util-hooks';
import { LayersIcon, NavRecordings } from '@va/icons';
import { useLocale, useTranslate } from '@va/localization';
import { PRIVACY_FEATURES } from '@va/standalone/shared/types';
import { HeaderCellV8 } from '@va/ui/components/data-table';
import { Button, PageItemType, VisitedPagesTooltip } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { addNumberSeparator } from '@va/util/helpers';
import { useMemo } from 'react';
import { CampaignDetail } from './CampaignDetail';
import { SessionDetails } from './SessionDetails';
import { mappedVisitorHistoryPageVisits } from './mappedVisitorHistoryPageVisits';

const columnHelper = createColumnHelper<VisitSession>();

export const useVisitorsHistoryColumns = () => {
  const { locale } = useLocale();
  const translate = useTranslate();
  const { isTrackingEnabled } = useCheckPrivacySetting();

  const columns = useMemo(
    () => [
      columnHelper.group({
        id: 'duration',
        meta: {
          cellClassName: 'justify-start! text-left!',
        },
        minSize: 260,
        header: (context) => (
          <HeaderCellV8 context={context} text={translate('visitorsHistory.table.headers.sessionDate')} />
        ),
        cell: ({ row }) => {
          const { sessionStart, duration } = row.original;
          return (
            <SessionDetails sessionDuration={duration} sessionStartAt={sessionStart} wrapperClassName='gap-15px!' />
          );
        },
      }),
      columnHelper.group({
        id: 'pageVisits',
        minSize: 110,
        header: (context) => <HeaderCellV8 context={context} text={translate('visitorsHistory.table.headers.pages')} />,
        cell: ({ row }) => {
          const { sessionStart, countryCode, pageVisits } = row.original;

          const isPageHistoryTrackingEnabled = isTrackingEnabled(PRIVACY_FEATURES.individualPageHistory, countryCode);
          const mappedPages: PageItemType[] = mappedVisitorHistoryPageVisits(pageVisits, locale);

          return (
            <TooltipWrapper
              interactive
              useDefaultStyle={false}
              content={
                <VisitedPagesTooltip
                  title={translate('card.latestVisitors.visitorsHistory.title')}
                  sessionStart={sessionStart}
                  items={mappedPages}
                  showVisitPagesAndDate
                  showSubTitle={false}
                  isPageHistoryTrackingEnabled={isPageHistoryTrackingEnabled}
                />
              }
            >
              <div>
                <Button
                  className='justify-center'
                  color='secondary'
                  text={addNumberSeparator(pageVisits?.length ?? 0, locale)}
                  icon={(_, color) => <LayersIcon color={color} />}
                />
              </div>
            </TooltipWrapper>
          );
        },
      }),
      columnHelper.accessor('countryCode', {
        header: (context) => (
          <HeaderCellV8 context={context} text={translate('visitorsHistory.table.headers.visitorDetails')} />
        ),
        cell: ({ getValue: getCountryCode, row }) => {
          const { status, location, id } = row.original;
          return (
            <VisitorDetailsCell
              sessionId={id}
              location={location}
              visitorType={status}
              countryCode={getCountryCode()}
            />
          );
        },
      }),
      columnHelper.accessor('referrer', {
        minSize: 150,
        maxSize: 200,
        header: (context) => (
          <HeaderCellV8 context={context} text={translate('visitorsHistory.table.headers.referrer')} />
        ),
        cell: ({ getValue }) => {
          return <ReferrerCell referrer={getValue()} />;
        },
      }),
      columnHelper.group({
        id: 'browser',
        minSize: 300,
        header: (context) => (
          <HeaderCellV8 context={context} text={translate('visitorsHistory.table.headers.recordingDetails')} />
        ),
        cell: ({ row }) => {
          const {
            ip,
            adCampaignKey,
            adCampaignSource,
            adCampaignMedium,
            adCampaignTerm,
            adCampaignContent,
            adCampaignLabel,
            browser,
            platform,
            device,
            display,
          } = row.original;
          return (
            <div className='flex gap-3 px-2'>
              <DetailsCell ip={ip} browser={browser} platform={platform} device={device} display={display} />
              <CampaignDetail
                campaignDetails={{
                  adCampaignKey,
                  adCampaignSource,
                  adCampaignMedium,
                  adCampaignTerm,
                  adCampaignContent,
                  adCampaignLabel,
                }}
              />
            </div>
          );
        },
      }),
      columnHelper.group({
        id: 'recording',
        minSize: 100,
        maxSize: 100,
        header: (context) => <HeaderCellV8 context={context} icon={<NavRecordings color='#969696' />} />,
        cell: ({ row }) => {
          const { sessionStart, id, hasRecording } = row.original;
          return (
            <VideoRecordingButton
              sourceTab={tabNames.visitorsHistory}
              sessionInfo={{
                unixTs: sessionStart,
                id,
                hasRecording,
              }}
            />
          );
        },
      }),
    ],
    [isTrackingEnabled, locale, translate],
  );

  return columns;
};
